import { getTranslatedText } from '@nile/components';
import { Condition, FormErrors, FormMeta, FormState } from '../types/interface';
import dayjs from 'dayjs';
import { MDMClientConfig } from '../DeviceSettings/types';

function getMarginLeft(level: number) {
  switch (level) {
    case 1:
      return 'ml-44';
    case 2:
      return 'ml-88';
    default:
      return '';
  }
}

// Utility function to check if element is visible within its scrollable container
function isElementInViewport(
  element: HTMLElement,
  scrollableParentSelector?: string
): boolean {
  const rect = element.getBoundingClientRect();

  let scrollContainer: HTMLElement | null = null;

  // If specific selector provided, find that container
  if (scrollableParentSelector) {
    scrollContainer = element.closest(scrollableParentSelector) as HTMLElement;
  }

  // If no scrollable container found, fall back to viewport
  if (!scrollContainer) {
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    return rect.top >= 0 && rect.bottom <= viewportHeight;
  }

  // Check visibility within the scrollable container
  const containerRect = scrollContainer.getBoundingClientRect();
  const isVisible =
    rect.top >= containerRect.top &&
    rect.bottom <= containerRect.bottom - 50 && // 50px is the space below the container
    rect.left >= containerRect.left &&
    rect.right <= containerRect.right;

  return isVisible;
}

const isConditionMet = (condition: Condition, values: FormState): boolean => {
  const dependentValue = values[condition.dependsOn];

  switch (condition.operator) {
    case 'equal':
      return dependentValue === condition.value;
    case 'notEqual':
      return dependentValue !== condition.value;
    default:
      return true;
  }
};

const clearHiddenFieldValues = (
  formValues: FormState,
  formFields: FormMeta[]
): FormState => {
  const clearedValues = { ...formValues };

  formFields.forEach((field) => {
    if (!field.condition) return;
    const isVisible = isConditionMet(field.condition, formValues);
    if (!isVisible && clearedValues[field.name] != null) {
      clearedValues[field.name] = null;
    }
  });

  return clearedValues;
};

const initializeFormFromFields = (
  formFields: FormMeta[],
  existingValues: FormState = {}
): FormState => {
  const initialValues: FormState = {};

  formFields.forEach((field) => {
    initialValues[field.name] =
      existingValues[field.name] ?? field.defaultValue ?? null;
  });

  return initialValues;
};

const hasFormErrors = (errors: FormErrors): boolean => {
  return Object.values(errors).some(
    (error) => error !== null && error !== undefined
  );
};

const getRemainingDays = (targetDate: string) => {
  const today = dayjs().startOf('day');
  const endDate = dayjs(targetDate, 'YYYY-MM-DD');
  const diffDays = endDate.diff(today, 'days');
  return diffDays > 0 ? diffDays : 0;
};

// Helper function to get configuration status
const getConfigStatus = (config?: MDMClientConfig) => {
  if (!config?.status)
    return {
      status: 'Not Configured',
      variant: 'warning',
      icon: 'Warning'
    };

  switch (config.status) {
    case 'Active':
      if (config.expiresInDays !== undefined && config.expiresInDays < 30) {
        return {
          status: 'Active',
          variant: 'warning',
          icon: 'CheckCircle'
        };
      }
      return {
        status: 'Active',
        variant: 'info',
        icon: 'CheckCircle'
      };
    case 'Expired':
      return {
        status: 'Expired',
        variant: 'critical',
        icon: 'ErrorRound'
      };
    default:
      return {
        status: config.status,
        variant: 'warning',
        icon: 'Warning'
      };
  }
};

// Helper function to get expiry information
const getExpiryInfo = (config?: MDMClientConfig, intl?: any) => {
  if (!config?.expiresInDays && config?.expiresInDays !== 0) return null;

  if (config.expiresInDays <= 0) {
    return {
      text: getTranslatedText(intl, 'quote.details.status.expired'),
      icon: 'ErrorCloseCircle'
    };
  }
  if (config.expiresInDays < 30) {
    return {
      text: getTranslatedText(
        intl,
        'client.details.subscription.remainingDays',
        { days: config.expiresInDays }
      ),
      icon: 'AIWarning'
    };
  }
  return null;
};

export {
  getMarginLeft,
  isElementInViewport,
  isConditionMet,
  clearHiddenFieldValues,
  initializeFormFromFields,
  hasFormErrors,
  getRemainingDays,
  getConfigStatus,
  getExpiryInfo
};
